import {Routes, TitleStrategy} from "@angular/router";
import {AppRoutes} from "./enums/routes";
import {LoginComponent} from "./_auth/login/login.component";
import {HomeComponent} from "./home/home.component";
import {AuthGuard} from "./_auth/auth.guard";
import {Roles} from "./_auth/x-enums/roles";
import {AuditLogComponent} from "./_modules/audit/audit-log.component";
import {ProfileComponent} from "./authentication/profile/profile.component";
import {SigningInComponent} from "./authentication/signing-in/signing-in.component";
import {InviteLandingComponent} from "./_auth/migration/invite-landing.component";
import {ChooseContextComponent} from "./_auth/context/choose-context.component";
import {CategoriesComponent} from "./categories/categories.component";
import {PermissionService} from "./_auth/permission.service";
import {ItemsComponent} from "./items/items-index/items.component";
import {CompositionsComponent} from "./compositions/compositions.component";
import {CompositionsTypeComponent} from "./compositions-type/compositions-type.component";
import {TagsComponent} from "./tags/tags.component";
import {LocationsComponent} from "./locations/locations.component";
import {ConfigurationsComponent} from "./configurations/configurations.component";
import {OrdersComponent} from "./orders/orders.component";
import {DevicesComponent} from "./_modules/signalRTracker/devices/devices.component";
import {DeviceOnboardingComponent, deviceOnboardingTitle} from "./_modules/device-onboarding/device-onboarding.component";
import {EmployeesComponent} from "./_modules/company/employees/employees.component";
import {EmployeeDetailsComponent} from "./_modules/company/employee-details/employee-details.component";
import {TelegramComponent} from "./_modules/company/telegram/telegram.component";
import {SuppliersComponent} from "./_modules/company/suppliers/suppliers.component";
import {SupplierSettingsComponent} from "./_modules/company/suppliers/supplier-settings/supplier-settings.component";
import {SettingsComponent} from "./_modules/company/settings/settings.component";
import {AdminUserComponent} from "./admin/user/user.component";
import {PatchesComponent} from "./admin/patches/patches.component";
import {OwnerShardComponent} from "./owner/shard/shard.component";
import {OwnerUserComponent} from "./owner/user/user.component";
import {OwnerCompanyComponent} from "./owner/company/company.component";
import {
  CompositionsSettingsPageComponent
} from "./compositions/compositions-settings-page/compositions-settings-page.component";
import {
  CompostionTypeSettingsComponent
} from "./compositions-type/compostion-type-settings/compostion-type-settings.component";
import {ItemsSettingsPageComponent} from "./items/items-settings-page/items-settings-page.component";
import {TagsSettingsPageComponent} from "./tags/tags-settings-page/tags-settings-page.component";
import {LocationSettingsComponent} from "./locations/location-settings/location-settings.component";
import {EventSettingsComponent} from "./management/events/event-settings/event-settings.component";
import {CategorySettingsComponent} from "./categories/category-settings/category-settings.component";
import {
  DeviceSettingsPageComponent
} from "./_modules/signalRTracker/devices/device-settings-page/device-settings-page.component";
import {ShardSettingsComponent} from "./owner/shard/shard-settings/shard-settings.component";
import {CompanySettingComponent} from "./owner/company/company-setting/company-setting.component";
import {EmployeeSettingsComponent} from "./_modules/company/employee/employee-settings/employee-settings.component";
import {ChangelogComponent} from "./changelog/changelog.component";
import {AutomationRoutes} from "./_modules/automation/x-models/x-enums/automation-routes";
import {
  TransactionWorkflowSettingsComponent
} from "./_modules/automation/workflows/transaction-workflow/transaction-workflow-settings/transaction-workflow-settings.component";
import {CompanyRoutes} from "./_modules/company/x-models/x-enums/company-routes";
import {
  EmploymentRelationshipSettingsComponent
} from "./_modules/company/employment-relationships/employment-relationship-settings/employment-relationship-settings.component";
import {
  EmployeeAttributesSettingsComponent
} from "./_modules/company/employee-attributes/employee-attributes-settings/employee-attributes-settings.component";
import {IndexComponent as AnalysisIndexComponent} from "./_modules/analytics/views/index/index.component";
import {ANALYTICS_ROUTES} from "./_modules/analytics/analytics.routes";
import {CompanyComponent} from "./_modules/company/company.component";
import {COMPANY_ROUTES} from "./_modules/company/company.routes";
import {AutomationComponent} from "./_modules/automation/automation.component";
import {AUTOMATION_ROUTES} from "./_modules/automation/automation.routes";
import {LocationComponent} from "./_modules/location/location.component";
import {LOCATION_ROUTES} from "./_modules/location/location.routes";
import {SelfServiceComponent} from "./_modules/self-service/self-service.component";
import {SELFSERVICE_ROUTES} from "./_modules/self-service/self-service.routes";
import {InventoryComponent} from "./_modules/inventory/inventory.component";
import {INVENTORY_ROUTES} from "./_modules/inventory/inventory.routes";
import {EmployeePanelComponent} from "./_modules/employee-panel/employee-panel.component";
import {EMPLOYEEPANEL_PROVIDERS, EMPLOYEEPANEL_ROUTES} from "./_modules/employee-panel/employee-panel.routes";
import {FormsCompletionComponent} from "./_modules/employee-panel/forms-completion/forms-completion.component";
import {ContactLinkingComponent} from "./_modules/employee-panel/contact-linking/contact-linking.component";
import {EmploymentPendingComponent} from "./_modules/employee-panel/employment-pending/employment-pending.component";
import {
  TwoFactorRequiredComponent
} from "./authentication/two-factor-required/two-factor-required.component";
import {OperatorComponent} from "./_modules/operator/operator.component";
import {OPERATORPANEL_ROUTES} from "./_modules/operator/operator.routes";
import {LegalComponent} from "./_modules/legal/legal.component";
import {LEGAL_ROUTES} from "./_modules/legal/legal.routes";
import {GEOLOCATIONTRACKER_ROUTES} from "./_modules/geolocation-tracker/geolocation-tracker.routes";
import {OwnerComponent} from "./_modules/owner/owner.component";
import {OWNER_ROUTES} from "./_modules/owner/owner.routes";
import {MailingComponent} from "./_modules/mailing/mailing.component";
import {MAILING_ROUTES} from "./_modules/mailing/mailing.routes";
import {Provider} from "@angular/core";
import {CheqmeTitleStrategy} from "./cheqme-title.strategy";

export const APP_ROUTES: Routes = [
  {
    path: AppRoutes.Login, component: LoginComponent,
    title: "Anmelden"
  }, // 'login'
  {
    // External sign-in handoff landing — unguarded (no session yet); exchanges the handoff then routes home.
    path: AppRoutes.AuthenticationSigningIn, component: SigningInComponent,
    title: "Anmeldung läuft"
  },
  {
    // Invitation landing (#279) — unguarded by necessity: since account creation happens BY redeeming, an
    // invitee arriving from their link has no session that could be guarded.
    path: AppRoutes.Invite, component: InviteLandingComponent,
    title: "Einladung"
  },
  {
    // Context chooser (#279): guarded (it needs a session to ask about) but carries NO `roles`, because the
    // whole point is that this session has not resolved which roles it will hold yet. AuthGuard lets it through
    // ahead of its shell-specific redirects for the same reason.
    path: AppRoutes.AuthenticationChooseContext, component: ChooseContextComponent,
    canActivate: [AuthGuard],
    title: "Arbeitsbereich wählen"
  },
  {
    path: AppRoutes.Home, component: HomeComponent, //''
    canActivate: [AuthGuard],
    title: "Startseite"
  },
  {
    path: AppRoutes.AuditLog, component: AuditLogComponent, // 'audit'
    canActivate: [AuthGuard], data: {roles: [Roles.Auditor]},
    title: "Audit-Protokoll"
  },
  {
    path: AppRoutes.AuthenticationProfile, component: ProfileComponent,
    canActivate: [AuthGuard],
    title: "Profil"
  },
  {
    path: AppRoutes.Categories, component: CategoriesComponent, //'categories'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_CategoryComponent().default},
    title: "Kategorien"
  },
  {
    path: AppRoutes.Items, component: ItemsComponent, // 'items'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_ItemComponent().default},
    title: "Items"
  },
  {
    path: AppRoutes.Compositions, component: CompositionsComponent, // 'compositions'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_CompositionsComponent().default},
    title: "Artikel"
  },
  {
    path: AppRoutes.CompositionTypes,
    component: CompositionsTypeComponent,
    canActivate: [AuthGuard],
    data: {roles: PermissionService.Permissions_CompositionTypesComponent().default},
    title: "Artikel-Kategorien"
  },
  {
    path: AppRoutes.Tags, component: TagsComponent, // 'tags'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_TagsComponent().default},
    title: "Tags"
  },
  {
    path: AppRoutes.Locations, component: LocationsComponent, // locations
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_LocationsComponent().default},
    title: "Verkaufspunkte"
  },
  {
    path: AppRoutes.Configurations,
    component: ConfigurationsComponent, // 'configurations'
    canActivate: [AuthGuard],
    data: {roles: PermissionService.Permissions_ConfigurationsComponent().default},
    title: "Preisprofile"
  },
  {
    path: AppRoutes.Orders, component: OrdersComponent, //'orders'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_OrderComponent().default},
    title: "Verkäufe"
  },
  {
    path: AppRoutes.DeviceOverview, component: DevicesComponent, // 'devices/overview'
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_DevicesComponent().default},
    title: "Geräte"
  },
  {
    // The register-onboarding page (#469 steps 5 + 7, one page with two tabs since #279). `routeData()` rather
    // than `.default`, so this route and the button that reaches it are driven by the SAME PermissionSet — and
    // it is gated on central roles only, matching DeviceEnrollmentController's own [Authorize]. The credentials
    // tab's set is identical (same [Authorize] on DeviceCredentialController); the page hides a tab whose set
    // fails, so a drift between the two would show as a missing tab, not as a 403.
    path: AppRoutes.DeviceOnboarding + '/:tab', component: DeviceOnboardingComponent, // 'devices/onboarding/:tab'
    canActivate: [AuthGuard], data: PermissionService.Permissions_DeviceEnrollmentComponent().routeData(),
    title: deviceOnboardingTitle
  },
  // The former separate screens, and the bare page path, land on their tab so old links keep working.
  {path: AppRoutes.DeviceOnboarding, redirectTo: AppRoutes.DeviceOnboarding + '/qr', pathMatch: 'full'},
  {path: AppRoutes.DeviceEnrollment, redirectTo: AppRoutes.DeviceOnboarding + '/qr', pathMatch: 'full'},
  {path: AppRoutes.DeviceCredentials, redirectTo: AppRoutes.DeviceOnboarding + '/credentials', pathMatch: 'full'},
  {
    // The merged employees page: the former separate list + worktime-stats overview in one screen (#140).
    path: AppRoutes.CompanyEmployee, component: EmployeesComponent, // 'company/employee'
    canActivate: [AuthGuard],
    // `routeData()` rather than `.default`: the menu entry and this route are then driven by the SAME
    // PermissionSet, so `Roles.CompanyOwner` (#279) cannot be true of one and not the other. `roles` stays the
    // AND-evaluated legacy id list; `anyRoles` is the OR-list AuthGuard short-circuits on.
    data: {...PermissionService.Permissions_EmployeeComponent().routeData(), payroll: true},
    title: "Mitarbeiter"
  },
  {
    path: AppRoutes.CompanyEmployeeDetails,
    component: EmployeeDetailsComponent, // 'company/employee/details',
    canActivate: [AuthGuard],
    data: {...PermissionService.Permissions_EmployeeDetailsComponent().routeData(), payroll: true},
    title: "Mitarbeiterprofil"
  },
  {
    path: AppRoutes.CompanyTelegram, component: TelegramComponent, // 'company/telegram'
    canActivate: [AuthGuard], data: PermissionService.Permissions_TelegramComponent().routeData(),
    title: "Telegram"
  },
  {
    path: AppRoutes.CompanySupplier, component: SuppliersComponent, // 'company/supplier'
    canActivate: [AuthGuard], data: PermissionService.Permissions_SuppliersComponent().routeData(),
    title: "Lieferanten"
  },
  {
    path: AppRoutes.CompanySupplierSettings,
    component: SupplierSettingsComponent, // 'company/supplier/settings'
    canActivate: [AuthGuard],
    data: PermissionService.Permissions_SuppliersComponent().routeData(),
    title: "Lieferant"
  },
  {
    path: AppRoutes.CompanySettings, component: SettingsComponent, // 'company/settings'
    // routeData() rather than `.default` alone: `.default` discards `anyRoles`, which is the only thing a
    // company-only CompanyOwner session matches on (#279). The three admin routes below keep `.default`
    // deliberately — see the sets' own comments.
    canActivate: [AuthGuard], data: PermissionService.Permissions_CompanySettingsComponent().routeData(),
    title: "Unternehmenseinstellungen"
  },
  {
    path: AppRoutes.AdminUsers, component: AdminUserComponent, //'admin/users'
    canActivate: [AuthGuard], data: {roles: PermissionService.admin.default},
    title: "Benutzer"
  },
  {
    path: AppRoutes.AdminPatches, component: PatchesComponent, //'admin/patches'
    canActivate: [AuthGuard], data: {roles: PermissionService.admin.default},
    title: "Patches"
  },
  {
    path: AppRoutes.OwnerShards, component: OwnerShardComponent, //'owner/shards'
    canActivate: [AuthGuard], data: {roles: PermissionService.owner.default},
    title: "Standorte"
  },
  {
    path: AppRoutes.OwnerUsers, component: OwnerUserComponent, //'owner/users'
    canActivate: [AuthGuard], data: {roles: PermissionService.owner.default},
    title: "Benutzer"
  },
  {
    path: AppRoutes.OwnerCompanies, component: OwnerCompanyComponent, //'owner/companies'
    canActivate: [AuthGuard], data: {roles: PermissionService.owner.default},
    title: "Unternehmen"
  },
  {
    path: AppRoutes.CompositionSettings,
    component: CompositionsSettingsPageComponent, // 'compositions'
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_composition_get.default},
    title: "Artikel"
  },
  {
    path: AppRoutes.CompositionTypeSettings,
    component: CompostionTypeSettingsComponent,
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_compositiontype_get.default},
    title: "Artikel-Kategorie"
  },
  {
    path: AppRoutes.ItemSettings,
    component: ItemsSettingsPageComponent, // "items/settings"
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_item_get.default},
    title: "Item"
  },
  {
    path: AppRoutes.TagSettings,
    component: TagsSettingsPageComponent, // "tags/settings"
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_tag_get.default},
    title: "Tag"
  },
  {
    path: AppRoutes.LocationSettings,
    component: LocationSettingsComponent, // "locations/settings"
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_location_get.default},
    title: "Verkaufspunkt"
  },
  {
    path: AppRoutes.EventSettings, component: EventSettingsComponent, // "events/settings"
    canActivate: [AuthGuard], data: {roles: PermissionService.inventory_event_get.default},
    title: "Event"
  },
  {
    path: AppRoutes.CategorySettings,
    component: CategorySettingsComponent,
    canActivate: [AuthGuard],
    data: {roles: PermissionService.inventory_category_get.default},
    title: "Kategorie"
  },
  {
    path: AppRoutes.DeviceSettings,
    component: DeviceSettingsPageComponent,
    canActivate: [AuthGuard],
    data: {roles: PermissionService.device_information_get.default},
    title: "Gerät"
  },
  {
    path: AppRoutes.OwnerShardSettings, component: ShardSettingsComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.owner.default},
    title: "Standort"
  },
  {
    path: AppRoutes.OwnerCompanySettings, component: CompanySettingComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.owner.default},
    title: "Unternehmen"
  },
  {
    path: AppRoutes.CompanyEmployeeSettings,
    component: EmployeeSettingsComponent,
    canActivate: [AuthGuard],
    data: PermissionService.company_employee_get.routeData(),
    title: "Mitarbeiter"
  },
  {
    path: AppRoutes.Changelog, component: ChangelogComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.Permissions_ChangelogComponent().default},
    title: "Changelog"
  },
  {
    path: AppRoutes.AutomationModule + "/" + AutomationRoutes.WorkflowsTransactionSettings,
    component: TransactionWorkflowSettingsComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.automation_workflow_get.default},
    title: "Workflow Einstellungen"
  },


  // modules
  {
    path: AppRoutes.CompanyModule + "/" + CompanyRoutes.EmployeeRelationshipsSettings,
    component: EmploymentRelationshipSettingsComponent,
    canActivate: [AuthGuard],
    data: PermissionService.company_employmentrelationship_get.routeData(),
    title: "Arbeitsverhältnis"
  },
  {
    path: AppRoutes.CompanyModule + "/" + CompanyRoutes.EmployeeAttributesSettings,
    component: EmployeeAttributesSettingsComponent,
    canActivate: [AuthGuard],
    data: PermissionService.company_employeeattribute_get.routeData(),
    title: "Mitarbeiterattribut"
  },
  {
    path: AppRoutes.Analytics, // 'analytics'
    component: AnalysisIndexComponent,
    title: "Analyse",
    children: ANALYTICS_ROUTES
  },
  {
    path: AppRoutes.CompanyModule,
    component: CompanyComponent,
    children: COMPANY_ROUTES
  },
  {
    path: AppRoutes.AutomationModule,
    component: AutomationComponent,
    children: AUTOMATION_ROUTES
  },
  {
    path: AppRoutes.LocationModule,
    component: LocationComponent,
    children: LOCATION_ROUTES
  },
  {
    path: AppRoutes.SelfServiceModule,
    component: SelfServiceComponent,
    children: SELFSERVICE_ROUTES
  },
  {
    path: AppRoutes.InventoryModule,
    component: InventoryComponent,
    children: INVENTORY_ROUTES,
  },
  {
    // Forms-only screen for a required-forms gated (restricted) employee session. Kept OUTSIDE the panel shell
    // (no sidebar/nav) so a restricted session has nowhere to navigate but the required forms. AuthGuard routes
    // restricted employees here and bounces non-restricted ones back to the panel.
    path: AppRoutes.FormsRequired,
    component: FormsCompletionComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.ep_base.default}
  },
  {
    // Contact-linking screen for a contact-gated (restricted) employee session (#558). Outside the panel shell
    // for the same reason as the forms screen above, and gated on the same employee role set — the contact
    // gate is employee-only, unlike the two-factor one below. AuthGuard routes restricted employees here and
    // bounces sessions with no contact business back to the panel.
    path: AppRoutes.ContactRequired,
    component: ContactLinkingComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.ep_base.default},
    title: "Kontakt verknüpfen"
  },
  {
    // Waiting screen for an employee whose employment has not started yet (#864). Outside the panel shell and
    // gated on the employee role set, like the forms and contact screens above. AuthGuard pins an
    // employment-pending session here (letting it visit the forms and contact screens) and bounces everybody
    // else back to the panel.
    path: AppRoutes.EmploymentPending,
    component: EmploymentPendingComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.ep_base.default},
    title: "Beschäftigung beginnt bald"
  },
  {
    // Two-factor remediation screen for an account-security restricted session (#282). Guarded, but with NO
    // `data.roles` — and that is the load-bearing difference from the forms screen above. `AccountSecurityLevel`
    // is a company-wide policy, so every kind of session can be restricted by it and every kind must reach the
    // one screen that lifts it; naming any role set here would put the excluded ones in a redirect loop —
    // sent here by the gate, refused entry by the role check, sent here again.
    //
    // AuthGuard owns both directions: it routes a restricted session here from everywhere else, and bounces a
    // no-longer-restricted one back off it.
    path: AppRoutes.TwoFactorRequired,
    component: TwoFactorRequiredComponent,
    canActivate: [AuthGuard],
    title: "Zwei-Faktor-Authentifizierung"
  },
  {
    path: AppRoutes.EmployeePanel,
    component: EmployeePanelComponent,
    canActivate: [AuthGuard], data: {roles: PermissionService.ep_base.default},
    children: EMPLOYEEPANEL_ROUTES,
    providers: EMPLOYEEPANEL_PROVIDERS
  },
  {
    // Standalone Operator panel (#205) — mirrors the EmployeePanel entry above: its own gated shell, its own
    // child routes. Operator is a baseline role held INSTEAD of ShardAdmin/Employee, so it is listed explicitly.
    path: AppRoutes.OperatorPanel,
    component: OperatorComponent,
    canActivate: [AuthGuard], data: {roles: [Roles.Operator]},
    children: OPERATORPANEL_ROUTES
  },
  {
    path: AppRoutes.LegalModule,
    component: LegalComponent,
    children: LEGAL_ROUTES
  },
  {
    path: AppRoutes.OwnerGeolocationTracking,
    children: GEOLOCATIONTRACKER_ROUTES
  },
  {
    path: AppRoutes.OwnerModule,
    component: OwnerComponent,
    children: OWNER_ROUTES
  },
  {
    path: AppRoutes.MailingModule,
    component: MailingComponent,
    children: MAILING_ROUTES
  },
  {
    path: '**', redirectTo: '/', pathMatch: "full"
  }
];

export const APP_ROUTING_PROVIDER: Provider = {
  provide: TitleStrategy,
  useClass: CheqmeTitleStrategy
}
